import { Group } from "@mantine/core";
import { AdminActionButton } from "./AdminActionButton";
import {
  abandonWalletMigration,
  notApplicable,
  reactivateWallet,
  redriveWalletMigration,
  waiveMigrationCardKeys,
} from "@/api/actions";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreDoc, useFirestoreQuery } from "@/firestore/hooks";
import type {
  SmartAccountOperationDoc,
  WalletDoc,
  WalletMigrationDoc,
  WithDocId,
} from "@/firestore/types";
import {
  abandonAvailability,
  isReactivationShape,
  reactivationCandidate,
  reactivationCurrentWallet,
  redriveAvailability,
} from "@/wallet/migrations";
import { waiveCardKeysAvailability } from "@/wallet/migrationCohorts";

/**
 * The migration detail's operator commands. Each button is keyed on the versions it is fenced
 * on, so a row that moves while the dialog is open closes it: the operator re-reviews the new
 * state instead of submitting the new version unseen.
 *
 * Waive card keys (the redrive's `waiveCardKeys`, superAdmin in every environment) is rendered only
 * in `installingTransferKeys`, or disabled once the row's waiver is used.
 *
 * Redrive reads the transfer legs' operations (`mainTransferSao` / `reserveTransferSao`, 0.75.0)
 * so it is not offered while a leg may be on chain. Reactivate is only rendered for an owner
 * rotation stopped at `installingTransferKeys` (the one topology its runbook approves); the
 * wallet it would retire (C) is the source's projected `currentWallet`, inferred from the
 * successor's onward rotation only on documents that predate the field.
 */
export function WalletMigrationActions({
  migration,
}: {
  migration: WithDocId<WalletMigrationDoc>;
}) {
  const source = useFirestoreDoc<WalletDoc>(COLLECTIONS.wallets, migration.oldWallet);
  const mainLeg = useFirestoreDoc<SmartAccountOperationDoc>(
    COLLECTIONS.smartAccountOperations,
    migration.mainTransferSao ?? null,
  );
  const reserveLeg = useFirestoreDoc<SmartAccountOperationDoc>(
    COLLECTIONS.smartAccountOperations,
    migration.reserveTransferSao ?? null,
  );
  const reactivationShape = isReactivationShape(migration);
  const inferCurrent =
    reactivationShape && !!source.data && source.data.currentWallet === undefined;
  const onward = useFirestoreQuery<WalletMigrationDoc>(
    COLLECTIONS.walletMigrations,
    { filters: [["oldWallet", "==", migration.newWallet ?? ""]], limit: 10 },
    inferCurrent,
  );
  const currentId =
    reactivationShape && source.data
      ? reactivationCurrentWallet({
          migration,
          source: source.data,
          successorMigrations: onward.data,
        }).id
      : null;
  const current = useFirestoreDoc<WalletDoc>(COLLECTIONS.wallets, currentId);

  const redrive = redriveAvailability(migration, [mainLeg.data?.state, reserveLeg.data?.state]);
  const abandon = abandonAvailability(migration, source.data);
  const reactivation = reactivationCandidate({
    migration,
    source: source.data,
    successorMigrations: onward.data,
    current: current.data,
  });
  const waiver = waiveCardKeysAvailability(migration);
  const fence = { id: migration.id ?? migration._docId, updatedAt: migration.updatedAt };

  return (
    <Group gap="xs">
      <AdminActionButton
        key={`redrive-${migration.updatedAt}`}
        action={
          redrive.offered
            ? redriveWalletMigration
            : notApplicable(redriveWalletMigration, redrive.note)
        }
        params={fence}
      />
      <AdminActionButton
        key={`abandon-${migration.updatedAt}`}
        action={
          abandon.offered
            ? abandonWalletMigration
            : notApplicable(abandonWalletMigration, abandon.note)
        }
        params={fence}
      />
      {waiver.shown ? (
        <AdminActionButton
          key={`waive-${migration.updatedAt}`}
          action={
            waiver.offered
              ? waiveMigrationCardKeys
              : notApplicable(waiveMigrationCardKeys, waiver.note)
          }
          params={fence}
        />
      ) : null}
      {reactivation ? (
        reactivation.offered ? (
          <AdminActionButton
            key={`reactivate-${migration.updatedAt}-${reactivation.sourceUpdatedAt}-${reactivation.currentWalletUpdatedAt}`}
            action={reactivateWallet}
            params={{
              walletId: reactivation.sourceWallet,
              updatedAt: reactivation.sourceUpdatedAt,
              currentWallet: reactivation.currentWallet,
              currentWalletUpdatedAt: reactivation.currentWalletUpdatedAt,
              migration: fence.id,
              migrationUpdatedAt: migration.updatedAt,
            }}
          />
        ) : (
          <AdminActionButton
            action={notApplicable(reactivateWallet, reactivation.note)}
            params={{
              walletId: migration.oldWallet,
              updatedAt: "",
              currentWallet: "",
              currentWalletUpdatedAt: "",
              migration: fence.id,
              migrationUpdatedAt: migration.updatedAt,
            }}
          />
        )
      ) : null}
    </Group>
  );
}
